/**
 * @fileoverview Syndicator Showdown — Protected Route Guard
 * @module components/layout/ProtectedRoute
 */

import React, { useState, useEffect } from 'react';
import { Navigate, useLocation }      from 'react-router-dom';
import { onAuthStateChanged }         from 'firebase/auth';
import { getDoc, doc }                from 'firebase/firestore';
import { auth, db }                   from '../../services/firebase';

// requireAdmin (preferred) and adminOnly (legacy alias) are both accepted
export const ProtectedRoute = ({ children, requireAdmin = false, adminOnly = false }) => {
  const location   = useLocation();
  const needsAdmin = requireAdmin || adminOnly;

  // undefined = still checking, null = no user, object = authenticated
  const [user,     setUser]     = useState(undefined);
  const [isAdmin,  setIsAdmin]  = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (cancelled) return;

      if (!u) {
        setUser(null);
        setIsAdmin(false);
        setChecking(false);
        return;
      }

      // Accept Google SSO as verified
      const isVerified = u.emailVerified || u.providerData.some(p => p.providerId === 'google.com');
      if (!isVerified) {
        setUser(null);
        setChecking(false);
        return;
      }

      setUser(u);

      if (needsAdmin) {
        try {
          const snap = await getDoc(doc(db, 'users', u.uid));
          if (!cancelled) {
            const role = snap.exists() ? (snap.data().role || 'player') : 'player';
            setIsAdmin(role === 'admin' || role === 'operator');
          }
        } catch {
          if (!cancelled) setIsAdmin(false);
        }
      }

      if (!cancelled) setChecking(false);
    });

    return () => { cancelled = true; unsub(); };
  }, [needsAdmin]);

  // Still waiting for Firebase — show spinner, do NOT redirect yet
  if (checking) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100dvh', background: '#07111F',
      }}>
        <div style={{
          width: '36px', height: '36px',
          border: '3px solid rgba(255,255,255,0.15)',
          borderTopColor: '#1A4FFF',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Auth check complete — no valid user
  if (!user) {
    return <Navigate to="/signin" state={{ from: location, reason: 'auth_required' }} replace />;
  }

  // Admin route but user isn't admin
  if (needsAdmin && !isAdmin) {
    return <Navigate to="/" state={{ from: location, reason: 'admin_required' }} replace />;
  }

  return children;
};

export default ProtectedRoute;
